import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import useWalletData from '../../hooks/useWalletData';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import firebaseFunctionsService from '../../services/firebaseFunctionsService';

export default function WithdrawalAccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = usePublicAuth();
  const { accountDoc, withdrawalTotals } = useWalletData(true);
  const [statusMessage, setStatusMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSetup = async () => {
    if (!user?.email) return;
    setLoading(true);
    setStatusMessage(null);
    try {
      const account = await firebaseFunctionsService.createConnectAccount(
        user.email,
        user.uid,
      );
      if (account?.accountId) {
        const link = await firebaseFunctionsService.createAccountLink(
          account.accountId,
        );
        if (link?.url) {
          window.open(link.url, '_blank', 'noopener');
          setStatusMessage({
            type: 'success',
            text: t('wallet.withdrawalAccount.onboardingOpened'),
          });
        }
      }
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text:
          error.message ||
          t('wallet.withdrawalAccount.unableToStartOnboarding'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!user?.uid) return;
    setLoading(true);
    setStatusMessage(null);
    try {
      const result = await firebaseFunctionsService.checkAccountStatus(
        user.uid,
      );
      setStatusMessage({
        type: result.hasStripeAccount ? 'success' : 'error',
        text: result.hasStripeAccount
          ? t('wallet.withdrawalAccountConnected', {
              accountId: result.stripeConnectAccountId.slice(-6),
            })
          : t('wallet.withdrawalAccount.accountNotFound'),
      });
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text:
          error.message || t('wallet.withdrawalAccount.unableToCheckStatus'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="pt-32 pb-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/wallet')}
            className="inline-flex items-center gap-2 text-gray-600 dark:text-slate-400 text-sm font-semibold mb-6 hover:text-gray-900 dark:hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('wallet.allTransactions.backToWallet')}
          </button>

          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-100 dark:border-slate-800 space-y-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <Banknote className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
                  {t('wallet.withdrawalAccount.withdrawalMethod')}
                </p>
                <h1 className="text-3xl font-black text-gray-900 dark:text-white">
                  {t('wallet.withdrawalAccount.title')}
                </h1>
              </div>
            </div>

            <p className="text-gray-600 dark:text-slate-400 leading-7">
              {t('wallet.withdrawalAccount.description')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 p-4">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest mb-1">
                  {t('wallet.withdrawalAccount.currentStatus')}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {accountDoc?.stripeConnectAccountId
                    ? t('wallet.withdrawalAccountConnected', {
                        accountId: accountDoc.stripeConnectAccountId.slice(-6),
                      })
                    : t('wallet.withdrawalAccountNotConnected')}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 p-4">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest mb-1">
                  {t('wallet.totalWithdrawals')}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('wallet.withdrawalsCount', {
                    count: withdrawalTotals.count,
                  })}
                </p>
              </div>
            </div>

            {statusMessage && (
              <div
                className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                  statusMessage.type === 'error'
                    ? 'border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'
                    : 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                }`}
              >
                {statusMessage.text}
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleSetup}
                disabled={loading}
                className="rounded-2xl bg-black dark:bg-slate-700 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading
                  ? t('wallet.processing')
                  : t('wallet.withdrawalAccount.startOnboarding')}
              </button>
              <button
                onClick={handleCheckStatus}
                disabled={loading}
                className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-5 py-3 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('wallet.checking')}
                  </span>
                ) : (
                  t('wallet.checkStatus')
                )}
              </button>
            </div>

            <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4 flex items-start gap-3 text-sm text-gray-700 dark:text-slate-300">
              <ShieldCheck className="h-5 w-5 text-indigo-500 dark:text-indigo-400 mt-0.5" />
              <p>{t('wallet.withdrawalAccount.securityNote')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
